import React from "react";

const ThreePTeachers = () => {
  return (
    <div className="bg-black text-white [&_p]:text-white [&_li]:text-gray-200 min-h-screen p-4">

      <div className="mx-auto max-w-4xl">

        <h1 className="text-lg lg:text-2xl font-bold text-yellow-200 mb-4">
          🧑‍🏫 3P for Teachers
        </h1>

        <h2 className="text-sm lg:text-lg text-purple-300 mb-4">
          Story: Meena Ma’am’s “One Hour” Mistake
        </h2>

        <p className="text-gray-300 mb-4">
          It is <b>6:15 PM</b> in Meena Ma’am’s house.
          After returning from school, she sits at her study table with a cup of tea.
        </p>

        <p className="text-gray-300 mb-4">
          In front of her lies a stack of <b>30 exam answer sheets</b> from her Class 9 algebra test.
        </p>

        <p className="text-gray-300 mb-4">
          She flips through a few pages and says to herself:
        </p>

        <p className="text-yellow-200 mb-6">
          “This shouldn’t take long… I’ll finish checking all papers in about one hour.”
        </p>

        <div className="bg-gray-900 p-4 rounded mb-6">

          <p className="text-gray-300 mb-2">Her evening plan:</p>

          <ul className="list-disc list-inside text-gray-300 space-y-1">
            <li>Finish paper checking by 7:15 PM</li>
            <li>Prepare tomorrow’s lesson plan by 8:00 PM</li>
            <li>Relax after dinner</li>
          </ul>

        </div>

        <h2 className="text-lg font-semibold text-purple-300 mb-3">
          What Actually Happened
        </h2>

        <ul className="list-disc list-inside text-gray-300 space-y-1 mb-6">
          <li>Paper 1 — Easy to check</li>
          <li>Paper 2 — Mostly correct</li>
          <li>Paper 3 — Correct answer but wrong method</li>
          <li>Paper 6 — Calculation mistakes require full recheck</li>
        </ul>

        <p className="text-gray-300 mb-4">
          By the time she reaches Paper 12, she notices something troubling.
        </p>

        <p className="text-red-300 mb-6">
          The clock already shows 7:30 PM — and only 12 papers are finished.
        </p>

        <p className="text-gray-300 mb-4">
          Finally she finishes the last paper and looks at the clock.
        </p>

        <div className="bg-gray-900 p-4 rounded mb-6">

          <p className="text-gray-300">
            Expected time: <span className="text-yellow-200">1 hour</span>
          </p>

          <p className="text-gray-300">
            Actual time: <span className="text-red-300">2.5 hours</span>
          </p>

        </div>

        <h2 className="text-lg font-semibold text-purple-300 mb-3">
          The Real Impact
        </h2>

        <p className="text-gray-300 mb-4">
          Now Meena Ma’am still needs to prepare tomorrow’s lesson plan,
          but she feels mentally exhausted.
        </p>

        <ul className="list-disc list-inside text-gray-300 space-y-1 mb-6">
          <li>The lesson becomes rushed</li>
          <li>Student questions remain unanswered</li>
          <li>Class engagement drops</li>
        </ul>

        <h2 className="text-lg font-semibold text-purple-300 mb-3">
          The Planning Skill Most Teachers Never Learn
        </h2>

        <p className="text-gray-300 mb-4">
          During a teacher training workshop, Meena Ma’am discovers a powerful concept:
        </p>

        <div className="bg-gray-900 p-4 rounded mb-6">
          <p className="text-yellow-200 font-semibold">
            Think5Whys PTM 3P Estimation
          </p>
          <p className="text-gray-300 text-sm">
            A structured way to estimate time using three scenarios.
          </p>
        </div>

        <h2 className="text-lg font-semibold text-purple-300 mb-3">
          How She Could Estimate Paper Checking
        </h2>

        <div className="bg-gray-900 p-4 rounded mb-6">

          <table className="w-full text-left text-sm">

            <thead className="text-yellow-200">
              <tr>
                <th>Estimate</th>
                <th>Time</th>
              </tr>
            </thead>

            <tbody className="text-gray-300">
              <tr>
                <td>Optimistic (O)</td>
                <td>60 minutes</td>
              </tr>

              <tr>
                <td>Most Likely (M)</td>
                <td>90 minutes</td>
              </tr>

              <tr>
                <td>Pessimistic (P)</td>
                <td>150 minutes</td>
              </tr>
            </tbody>

          </table>

        </div>

        <h2 className="text-lg font-semibold text-purple-300 mb-3">
          Think5Whys PTM 3P Calculation
        </h2>

        <div className="bg-gray-900 p-4 rounded mb-6">

          <p className="text-gray-300 mb-2">
            <b>Triangular Estimate</b>
          </p>

          <p className="text-yellow-200 mb-4">
            (60 + 90 + 150) / 3 = 100 minutes
          </p>

          <p className="text-gray-300 mb-2">
            <b>PERT Estimate</b>
          </p>

          <p className="text-yellow-200">
            (60 + 4×90 + 150) / 6 ≈ 95 minutes
          </p>

        </div>

        <h2 className="text-lg font-semibold text-purple-300 mb-3">
          A Better Evening Plan
        </h2>

        <div className="bg-gray-900 p-4 rounded mb-6">

          <table className="w-full text-left text-sm">

            <thead className="text-yellow-200">
              <tr>
                <th>Task</th>
                <th>Time</th>
              </tr>
            </thead>

            <tbody className="text-gray-300">
              <tr>
                <td>Answer Sheet Checking</td>
                <td>95 minutes</td>
              </tr>

              <tr>
                <td>Lesson Planning</td>
                <td>40 minutes</td>
              </tr>

              <tr>
                <td>Buffer</td>
                <td>10 minutes</td>
              </tr>
            </tbody>

          </table>

        </div>

        <h2 className="text-lg font-semibold text-purple-300 mb-3">
          Using 3P in Daily Teaching Life
        </h2>

        <ul className="list-disc list-inside text-gray-300 space-y-1 mb-6">
          <li>Preparing lesson plans</li>
          <li>Designing exam papers</li>
          <li>Checking assignments</li>
          <li>Organizing school events</li>
          <li>Preparing presentations</li>
          <li>Mentoring students</li>
        </ul>

        <h2 className="text-lg font-semibold text-purple-300 mb-3">
          The Final Realization
        </h2>

        <p className="text-gray-300 mb-2">
          One evening Meena Ma’am smiled while finishing her work on time.
        </p>

        <p className="text-yellow-200 text-lg">
          “I used to guess my workload… now I estimate it.”
        </p>

      </div>

    </div>
  );
};

export default ThreePTeachers;